import { ApplicationConfig, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MsalBroadcastService, MsalService, MSAL_INSTANCE } from '@azure/msal-angular';
import { AuthenticationResult, PublicClientApplication } from '@azure/msal-browser';
import { provideBtTooltips } from '@bluetitanai/bt-chrome';
import { firstValueFrom } from 'rxjs';

import { APP_ROUTES } from './app.routes';
import { authInterceptor } from './auth/auth.interceptor';
import { msalConfig } from './auth/auth-config';
import { CiamAuthService, SUPPORT_RAIL_KEY } from './auth/ciam-auth.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(APP_ROUTES),
    provideAnimations(),
    provideHttpClient(withInterceptors([authInterceptor])),
    // Shared tooltip config + auto-hide, canonical across BT surfaces (design/support-console-tooltips).
    // Sets container:'body', placement:'auto', openDelay/closeDelay, and installs the global
    // auto-hide hook — see @bluetitanai/bt-chrome for the single source of truth.
    provideBtTooltips(),
    { provide: MSAL_INSTANCE, useValue: new PublicClientApplication(msalConfig) },
    MsalService,
    MsalBroadcastService,
    // MSAL Browser v5 REQUIRES instance.initialize() before ANY other MSAL call, and
    // MsalBroadcastService.inProgress$ only leaves `Startup` once the initial redirect
    // handling completes. Without this, authenticatedGuard sees inProgress !== None,
    // never fires loginRedirect, and the app renders a blank page (no error, because
    // the guard avoids the call that would throw). initialize() then
    // handleRedirectObservable() — the latter processes the return leg AND drives
    // inProgress$ to None — must run before the router/guards evaluate.
    provideAppInitializer(() => initializeMsal(inject(MsalService), inject(CiamAuthService))),
  ],
};

/**
 * MSAL Browser v5 REQUIRES instance.initialize() before ANY other MSAL call, and
 * MsalBroadcastService.inProgress$ only leaves `Startup` once the initial redirect
 * handling completes. Without this, authenticatedGuard sees inProgress !== None,
 * never fires loginRedirect, and the app renders a blank page (no error — the guard
 * avoids the call that would throw). Runs as an APP_INITIALIZER so it completes
 * before the router/guards evaluate.
 *
 * handleRedirectObservable() processes the return leg AND drives inProgress$ to None,
 * but does NOT set the ACTIVE account — and isSignedIn()/the guard read
 * getActiveAccount(). So set the active account from the redirect result, or from a
 * cached session on a normal load, or the app would block even after a successful login.
 * Exported (not inlined) so the sequence is unit-testable against a faked MsalService.
 */
export async function initializeMsal(msal: MsalService, ciam: CiamAuthService): Promise<void> {
  await msal.instance.initialize();
  await ciam.initialize();

  // Rail-gate the redirect return. The response hash carries exactly one instance's
  // state; the two instances have different clientIds (separate token caches), so the
  // workforce instance cannot match a CIAM response — it would THROW state_not_found and
  // clear the hash before the CIAM instance ever reads it (a silent partner-login break).
  // The persisted rail names the owner, so process the return on that instance ONLY.
  if (sessionStorage.getItem(SUPPORT_RAIL_KEY) === 'partner' && ciam.isConfigured()) {
    await ciam.handleRedirect();
    return;
  }

  // Staff rail (the default, and every load where partner is not the active rail):
  // the workforce redirect handling below is unchanged from before the partner rail.
  let result: AuthenticationResult | null = null;
  let redirectFailed = false;
  try {
    result = await firstValueFrom(msal.handleRedirectObservable());
  } catch (err) {
    // A failed/denied redirect return (access_denied, consent declined, a CJIS
    // conditional-access / MFA / AAL2 failure, an unprovisioned staff account, or a
    // state mismatch) must NOT reject the app initializer — that aborts Angular
    // bootstrap and reproduces the very blank-page failure this fixes. Swallow so the
    // shell loads.
    redirectFailed = true;
    // eslint-disable-next-line no-console
    console.error('MSAL redirect handling failed', err);
  }

  // A successful return names its account explicitly — always honor it.
  if (result?.account) {
    msal.instance.setActiveAccount(result.account);
    return;
  }
  // After a FAILED return, do NOT rehydrate a cached account: the failure may belong to
  // a DIFFERENT user than the one cached (e.g. user B is denied on a browser that still
  // has user A cached), and silently activating A would be wrong (codex, 2026-08-10).
  // Leave none active; the guard drives an explicit login.
  // FOLLOW-UP (codex + code-review, 2026-08-10): a PERSISTENT failure (e.g. an
  // unprovisioned account) then loops /callback → guard → loginRedirect. Needs a bounded
  // retry / surfaced error screen — tracked, out of scope for this bootstrap fix. Still
  // strictly better than the blank page this replaces.
  if (redirectFailed) {
    return;
  }
  // Normal (non-redirect) load: honor an already-active session, else auto-select a
  // cached account ONLY when unambiguous (exactly one) — with several cached, index [0]
  // is not guaranteed to be the intended user (codex + code-review, 2026-08-10).
  const active = msal.instance.getActiveAccount();
  if (active) {
    msal.instance.setActiveAccount(active);
    return;
  }
  const accounts = msal.instance.getAllAccounts();
  if (accounts.length === 1) {
    msal.instance.setActiveAccount(accounts[0]);
  }
}
